<script lang="ts" setup>
import { Button, Icon, Page, Section } from "@/components/Ui";
import { BaseAffiliate } from "@/components/Sections";
import CardsForArbitration from "@/components/Sections/CardsForArbitration.vue";
import CardsBestConditions from "@/components/Sections/CardsBestConditions.vue";
import CardsSecond from "@/components/Sections/CardsSecond.vue";
import CardsFaq from "@/components/Sections/CardsFaq.vue";
import CardHeroExternal from "@/components/Cards/CardHeroExternal.vue";

import { computed, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useTextDirection } from "@vueuse/core";
import { faqForCards } from "@/config/faq";
import {
  cards,
  landingCards,
  type TCard,
  type TLandingCard,
} from "@/config/cards";
import { onDashboard, onEvent, onLink } from "@/helpers/events";
import { CardToolbar, CardWithAuth } from "@/components/Cards";

const { t, locale } = useI18n();
const localePath = useLocalePath();
const dir = useTextDirection();
const route = useRoute();
const router = useRouter();
const cardData = ref();
const card = computed<any>(() => {
  let h1: string = "";
  let seoTitle: string = "";
  let seoDescription: string = "";
  let buttonText: string = "";

  if (cardData.value?.external) {
    h1 =
      cardData.value?.locale_h1[locale.value] || cardData.value?.locale_h1?.en;
    seoTitle =
      cardData.value?.locale_title[locale.value] ||
      cardData.value?.locale_title?.en;
    seoDescription =
      cardData.value?.locale_description[locale.value] ||
      cardData.value?.locale_description?.en;
    buttonText =
      cardData.value?.locale_button[locale.value] ||
      cardData.value?.locale_button?.en;
  } else {
    h1 = t("landing.cards.h1", { p: cardData.value?.title });
    seoTitle = t("landing.cards.title", { p: cardData.value?.title });
    seoDescription = t("landing.cards.description", {
      p: cardData.value?.title,
    });
    buttonText = t("btn.issueCard");
  }

  const buttonAction = async () => {
    if (cardData.value?.external) {
      if (cardData.value?.button_link?.includes("app.pst.net")) {
        await onDashboard(
          `landing_${cardData.value?.slug}_button-1`,
          cardData.value?.button_link.split("app.pst.net").pop(),
          {},
        );
      } else if (cardData.value?.button_link?.includes("pst.net")) {
        await onEvent(
          `landing_${cardData.value?.slug}_button-1`,
          router.push(
            localePath(cardData.value?.button_link.split("pst.net").pop()),
          ),
          {},
        );
      } else {
        await onLink(
          `landing_${cardData.value?.slug}_button-1`,
          cardData.value?.button_link,
          {},
          false,
        );
      }
    } else {
      await onEvent(
        `landing_${cardData.value?.slug}_button-1`,
        router.push(localePath("/private")),
        {},
      );
    }
  };

  return {
    h1,
    card: cardData.value?.svg_card,
    logo: cardData.value?.svg_logo,
    background: cardData.value?.background,
    buttonText,
    buttonAction,
    seoTitle,
    seoDescription,
    external: cardData.value?.external || false,
    transaction_fee: cardData.value?.transaction_fee,
    deposit_fee: cardData.value?.deposit_fee,
    monthly_payment: cardData.value?.monthly_payment,
  };
});
const localFaqItems = computed<any>(() => {
  return faqForCards.find((card: { type: string }) => card.type === "adv")
    ?.items;
});
const getExternalFaqItems = (component: any) => {
  return component?.faq_item?.map((item: any) => {
    return {
      external: true,
      title: item.locale_title[locale.value] || item.locale_title?.en,
      content: item.locale_content[locale.value] || item.locale_content?.en,
    };
  });
};
const getCard = async () => {
  const landingCardFromConfig = landingCards?.find(
    (card: TLandingCard) => card.slug === route.params.slug,
  );
  if (landingCardFromConfig) {
    cardData.value = landingCardFromConfig;
  } else {
    const params = {
      // fields: ["title", "slug", "svg_card", "svg_logo"],
      "populate[sections][populate]": "*",
      "filters[slug][$eq]": route.params.slug,
    };
    const result = await $fetch("/api/cards", { params });
    console.log(result);
    if (result?.data?.length) {
      cardData.value = {
        external: true,
        ...result?.data[0]?.attributes,
      };
    } else {
      throw createError({
        statusCode: 404,
      });
    }
  }
};
await getCard();

useSeoMeta({
  title: card.value?.seoTitle,
  description: card.value?.seoDescription,
  ogTitle: card.value?.seoTitle,
  ogDescription: card.value?.seoDescription,
});

useHead({
  htmlAttrs: {
    class: "default-container",
  },
});
const direction = ref<string | null>(null);
onNuxtReady(() => {
  // Detect scroll up
  let oldScrollY = window.scrollY;
  window.onscroll = function () {
    if (oldScrollY < window.scrollY) {
      direction.value = "down";
    } else {
      direction.value = "up";
    }
    oldScrollY = window.scrollY;
  };
});
watch(
  () => direction.value,
  (newVal, oldVal) => {
    const root = document.documentElement;
    root.classList.add(`dir-${newVal}`);
    root.classList.remove(`dir-${oldVal}`);
  },
);
onUnmounted(() => {
  const root = document.documentElement;
  root.classList.remove(`dir-${direction.value}`);
  direction.value = null;
});
const toolbarCards = computed<TCard[]>(() => {
  return cards
    .filter((card: { hidden: boolean }) => !card.hidden)
    .sort(function (x, y) {
      return x.type === "ultima" ? -1 : y.type === "ultima" ? 1 : 0;
    })
    .filter((card: { type: string }) => card.type !== "black");
});
</script>
<template>
  <Page :class="landingCard.root">
    <CardToolbar
      :active="route.params.slug === 'facebook' ? 'fb' : route.params.slug"
      :cards="toolbarCards"
      :class="[landingCard.toolbar, landingCard[`toolbar-dir-${direction}`]]"
      :is-down="direction === 'down'"
    />

    <!--    {{cardData.sections}}-->
    <template v-if="!cardData.external">
      <section
        v-if="card"
        :style="`background: ${card.background}`"
      >
        <div
          :class="landingCard.content"
          class="container"
        >
          <h1
            :class="[
              landingCard.secondTitle,
              landingCard[`secondTitle-${$i18n.locale}`],
            ]"
          >
            {{ card?.h1 }}
          </h1>
          <p class="mb-8 text-center">
            {{ t("block.homeHero2.slide16.p4") }}
          </p>
          <Button
            :class="landingCard.button"
            :icon-class="dir === 'ltr' ? '' : 'rotate-180'"
            :title="card.buttonText"
            icon="arrow-outline-right"
            icon-pos="right"
            size="small"
            @click="card.buttonAction()"
          />
          <div
            class="pt-[68px] max-w-[560px] mx-auto -mt-[100px] translate-y-[100px]"
          >
            <div class="relative flex justify-center">
              <template v-if="!card.external">
                <template v-if="cardData.slug === 'facebook'">
                  <Icon
                    name="pst-fb"
                    path="cards"
                  />
                </template>
                <template v-else-if="cardData.slug === 'google'">
                  <Icon
                    name="pst-google"
                    path="cards"
                  />
                </template>
                <template v-else-if="cardData.slug === 'tiktok'">
                  <Icon
                    name="pst-tiktok"
                    path="cards"
                  />
                </template>
                <template v-else>
                  <Icon
                    class="w-full"
                    name="pst-default"
                    path="cards"
                  />
                  <Icon
                    :class="[
                      landingCard.serviceIcon,
                      landingCard[`serviceIcon-${cardData.slug}`],
                    ]"
                    :name="cardData.slug"
                    path="services"
                  />
                </template>
              </template>
              <div v-else>
                <span
                  v-if="card.card && !card.logo"
                  :class="landingCard.card"
                  v-html="card.card"
                />
                <template v-else>
                  <Icon
                    name="pst-default"
                    path="cards"
                  />
                  <span
                    v-if="card.logo"
                    :class="[
                      landingCard.serviceIcon,
                      landingCard[`serviceIcon-external`],
                    ]"
                    v-html="card.logo"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div class="gap-[86px] lg:gap-[136px] mt-[136px] flex flex-col pb-[70px]">
        <Section
          v-if="!card.external"
          :title-class="landingCard['root-h2']"
          class="mt-[90px]"
        >
          <template #title>
            {{ $t("cards.instantCardIssuance") }}
          </template>
          <CardWithAuth
            :email-button-event="`landing_${route.params.slug}_button-2.3`"
            :google-button-event="`landing_${route.params.slug}_button-2.1`"
            :telegram-button-event="`landing_${route.params.slug}_button-2.2`"
          >
            <template #card>
              <div class="relative">
                <template v-if="cardData.slug === 'facebook'">
                  <Icon
                    name="pst-fb"
                    path="cards"
                  />
                </template>
                <template v-else-if="cardData.slug === 'google'">
                  <Icon
                    name="pst-google"
                    path="cards"
                  />
                </template>
                <template v-else-if="cardData.slug === 'tiktok'">
                  <Icon
                    name="pst-tiktok"
                    path="cards"
                  />
                </template>
                <template v-else>
                  <Icon
                    name="pst-default"
                    path="cards"
                  />
                  <Icon
                    :name="cardData.slug"
                    path="services"
                    style="
                      display: block;
                      width: auto;
                      height: auto;
                      max-width: 120px;
                      max-height: 40px;
                      right: 16px;
                      top: 16px;
                      position: absolute;
                    "
                  />
                </template>
              </div>
            </template>
          </CardWithAuth>
        </Section>
        <CardsSecond />
        <CardsBestConditions />
        <CardsForArbitration />
        <CardsFaq
          :faq-items="localFaqItems"
          :section-class="landingCard.faq"
          :section-title-class="landingCard.sectionTitle"
          class="-mb-[80px]"
        />
      </div>
    </template>
    <template v-else>
      <div
        v-if="cardData.sections.length"
        :class="{
          '-mb-[80px]':
            cardData.sections[cardData.sections.length - 1].__component ===
            'components.faq',
          'mt-[70px] md:mt-[90px]':
            cardData.sections[0].__component !== 'components.cards-hero',
        }"
        class="gap-[86px] lg:gap-[136px] flex flex-col pb-[70px]"
      >
        <div
          v-for="(section, index) in cardData.sections"
          :key="index"
          :style="`order: ${index}`"
        >
          <section
            v-if="section.__component === 'components.cards-hero'"
            :style="`background: ${card.background}`"
          >
            <div
              :class="landingCard.content"
              class="container"
            >
              <h1
                :class="[
                  landingCard.title,
                  landingCard[`title-${$i18n.locale}`],
                ]"
              >
                {{ card?.h1 }}
              </h1>
              <Button
                :class="landingCard.button"
                :icon-class="dir === 'ltr' ? '' : 'rotate-180'"
                :title="card.buttonText"
                icon="arrow-outline-right"
                icon-pos="right"
                size="small"
                @click="card.buttonAction()"
              />
              <div
                class="pt-[68px] max-w-[560px] mx-auto -mt-[100px] translate-y-[100px]"
              >
                <div class="relative">
                  <template v-if="!card.external">
                    <Icon
                      name="pst-default"
                      path="cards"
                    />
                    <Icon
                      :class="[
                        landingCard.serviceIcon,
                        landingCard[`serviceIcon-${cardData.slug}`],
                      ]"
                      :name="cardData.slug"
                      path="services"
                    />
                  </template>

                  <template v-else>
                    <span
                      v-if="card.card && !card.logo"
                      :class="landingCard.card"
                      v-html="card.card"
                    />
                    <template v-else>
                      <Icon
                        name="pst-default"
                        path="cards"
                      />
                      <span
                        v-if="card.logo"
                        :class="[
                          landingCard.serviceIcon,
                          landingCard[`serviceIcon-external`],
                        ]"
                        v-html="card.logo"
                      />
                    </template>
                  </template>
                </div>
              </div>
            </div>
          </section>

          <CardsSecond
            v-if="section.__component === 'components.cards-native-and-types'"
          />
          <CardsBestConditions
            v-if="section.__component === 'components.cards-best-conditions'"
          />
          <CardsForArbitration
            v-if="section.__component === 'components.cards-for-arbitration'"
          />
          <CardsFaq
            v-if="section.__component === 'components.faq'"
            :faq-items="getExternalFaqItems(section) || []"
            :section-class="landingCard.faq"
            :section-title-class="landingCard.sectionTitle"
          />
          <section
            v-if="section.__component === 'components.content'"
            :class="component.content"
            class="py-[80px]"
          >
            <div class="container">
              <h2>{{ section.locale_title[$i18n.locale] }}</h2>
              <p>{{ section.locale_content[$i18n.locale] }}</p>
            </div>
          </section>

          <CardHeroExternal
            v-if="section.__component === 'components.cards-hero-with-utp'"
            :button-event="`landing_${cardData.value?.slug}_button-1`"
            :data="section"
          />
        </div>
      </div>
    </template>
    <BaseAffiliate />
  </Page>
</template>

<style lang="scss" module="landingCard">
.root {
  @apply flex flex-col;
  @apply mt-[70px] md:mt-[90px];

  &-h2 {
    @apply font-bold text-[36px] leading-none text-center;
  }
}

.content {
  @apply pt-[70px];
}

.title {
  @apply leading-none font-black text-neutral-800 pb-6 414:pb-10 text-center;
  &-en,
  &-id,
  &-da,
  &-et,
  &-hu,
  &-lt,
  &-nb,
  &-ro,
  &-sl,
  &-cs,
  &-el,
  &-fi,
  &-lv,
  &-nl,
  &-sk,
  &-sv,
  &-he,
  &-be,
  &-it,
  &-es,
  &-fr,
  &-pt,
  &-tr,
  &-ua,
  &-ja,
  &-zh,
  &-th,
  &-pl,
  &-hi,
  &-ar,
  &-bn,
  &-ko,
  &-vi,
  &-sw {
    @apply text-[40px] xxs:text-[44px] 414:text-[52px] xs:text-[80px];
  }

  &-zh {
    @apply leading-[1.1];
  }

  &-de {
    @apply text-[38px] xxs:text-[44px] 414:text-[50px] xs:text-[80px];
  }

  &-bg {
    @apply text-[40px] xxs:text-[44px] 414:text-[52px] xs:text-[80px];
  }

  &-ru {
    @apply mx-auto text-[40px] xxs:text-[44px] 414:text-[50px] xs:text-[80px];
  }

  &-ja {
    @apply leading-[1.3];
  }
}

.secondTitle {
  @apply sm:mt-5 mb-7 text-center font-black leading-none;
  &-en,
  &-id,
  &-da,
  &-et,
  &-hu,
  &-lt,
  &-nb,
  &-ro,
  &-sl,
  &-cs,
  &-el,
  &-fi,
  &-lv,
  &-nl,
  &-sk,
  &-sv,
  &-he,
  &-be,
  &-bg,
  &-pt,
  &-ua,
  &-tr,
  &-zh,
  &-it,
  &-fr,
  &-es,
  &-de,
  &-ja,
  &-th,
  &-pl,
  &-hi,
  &-ar,
  &-bn,
  &-ko,
  &-vi,
  &-sw {
    @apply text-[40px];
  }

  &-ru {
    @apply text-[30px] xxs:text-[32px] 414:text-[40px];
  }

  &-ja,
  &-tr,
  &-zh {
    @apply leading-[1.2];
  }
}

.serviceIcon {
  @apply absolute top-[20px] right-[20px] h-auto;

  &-facebook {
    @apply w-[104px] xxs:w-[130px] xs:w-[162px];
    @apply md:w-auto;
  }

  &-google {
    @apply w-[104px] xxs:w-[130px] xs:w-[142px];
    @apply md:w-auto;
  }

  &-instagram {
    @apply w-[122px] xxs:w-[138px] xs:w-[162px];
    @apply md:w-auto;
  }

  &-twitter {
    @apply w-6 xxs:w-7 xs:w-8;
    @apply md:w-auto;
  }

  &-snapchat {
    @apply w-6 xxs:w-8 xs:w-10;
    @apply md:w-auto;
  }

  &-pinterest,
  &-outbrain,
  &-quora,
  &-reddit,
  &-tiktok,
  &-microsoft {
    @apply w-[104px] xxs:w-[138px] xs:w-[142px];
    @apply md:w-auto;
  }

  &-linkedin {
    @apply w-[104px] xxs:w-[130px] xs:w-[162px];
    @apply md:w-auto;
  }

  &-taboola {
    @apply w-[104px] xxs:w-[138px] xs:w-[142px];
    @apply md:w-auto;
  }

  &-external {
    @apply w-[104px] xxs:w-[130px] xs:w-[162px];
    @apply md:w-auto;
  }
}

.button {
  @apply mx-auto;
}

.sectionTitle {
  @apply font-bold text-[36px] leading-none text-center;
}

.faq {
  @apply bg-neutral-900 py-[80px] text-white translate-y-[1px];
}

.card {
  @apply w-full h-auto max-w-[560px];
  & svg {
    @apply w-full h-auto;
  }
}

.toolbar {
  @apply transition-all transform-gpu w-full md:sticky bg-white sm:backdrop-blur-md sm:bg-opacity-[0.85];

  &-dir {
    &-up {
      @apply md:top-[70px] z-[1000];
    }

    &-down,
    &-null {
      @apply top-0 md:z-[1100];
    }
  }
}
</style>

<style lang="scss" module="component">
.content {
  @apply py-[80px];

  & > div {
    @apply flex flex-col gap-10 text-center;
  }

  & h2 {
    @apply font-black text-neutral-800 text-center max-w-[970px] mb-0 mx-auto;
    @apply text-[40px] xs:text-[54px] sm:text-[60px];
    @apply leading-[1.2];
  }

  & p {
    @apply text-neutral-800 text-[24px] text-center;
  }
}
</style>

<style lang="scss" module>
.tariff {
  @apply justify-center flex flex-row gap-5 lg:w-fit lg:max-w-full mx-auto mb-8;

  & > li {
    @apply flex flex-col w-1/3 lg:w-[140px] text-center;

    & span {
      &:first-child {
        @apply text-[25px] text-neutral-900;
      }

      &:last-child {
        @apply text-[12px] xxs:text-[14px] xs:text-[16px] text-neutral-700;
      }
    }
  }

  &-old {
    @apply hidden lg:inline-flex opacity-60 not-italic line-through px-2;
  }
}
</style>
